import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useGame, BUILDING_COIN_INCOME } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useLang } from "@/contexts/LanguageContext";

export default function ProfileCard() {
  const { user, logout } = useAuth();
  const { blocks } = useGame();
  const { isDark } = useTheme();
  const { t, lang } = useLang();
  const [copied, setCopied] = useState(false);

  if (!user) return null;

  const myBlocks = Object.values(blocks).filter((b) => b.ownerId === user.id);
  const hourlyIncome = myBlocks.reduce(
    (sum, b) => sum + b.buildings.reduce((s, bld) => s + BUILDING_COIN_INCOME[bld.type], 0),
    0
  );

  const joinDate = new Date(user.joinedAt).toLocaleDateString(lang === "ja" ? "ja-JP" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const handleCopyId = () => {
    navigator.clipboard.writeText(user.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";

  return (
    <div className={`rounded-2xl border p-6 mb-8 ${cardBg}`}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl">
            <span className="text-black text-3xl font-black">
              {user.username.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="absolute -bottom-1 -right-1 w-5 h-5 flex items-center justify-center bg-emerald-400 rounded-full border-2 border-[#0f1015]">
            <i className="ri-check-line text-black text-xs"></i>
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className={`text-xl font-black ${textPrimary}`}>{user.username}</h1>
            <span className="text-xs bg-amber-400/15 text-amber-400 border border-amber-400/30 rounded-full px-2.5 py-0.5 font-semibold whitespace-nowrap">
              {t("playerBadge")}
            </span>
          </div>
          <p className={`text-sm mb-3 ${textSecondary}`}>{user.email}</p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-1.5">
              <i className={`ri-calendar-line text-xs ${textSecondary}`}></i>
              <span className={`text-xs ${textSecondary}`}>{joinDate} {t("joinedAt")}</span>
            </div>
            <button
              onClick={handleCopyId}
              className={`flex items-center gap-1.5 cursor-pointer transition-colors ${textSecondary} hover:text-amber-400`}
            >
              <i className={`text-xs ${copied ? "ri-check-line text-emerald-400" : "ri-fingerprint-line"}`}></i>
              <span className="text-xs font-mono">{copied ? t("copiedId") : user.id}</span>
            </button>
          </div>
        </div>

        {/* Coin Balance */}
        <div className="flex-shrink-0 text-right">
          <div className="bg-amber-400/10 border border-amber-400/20 rounded-2xl px-6 py-4">
            <p className={`text-xs mb-1 ${textSecondary}`}>{t("coinBalance")}</p>
            <p className="text-3xl font-black text-amber-400">{user.coins.toLocaleString()}</p>
            <p className={`text-xs mt-1 ${textSecondary}`}>
              <span className="text-emerald-400 font-semibold">+{hourlyIncome}</span> {t("incomePerHour")}
            </p>
          </div>
        </div>
      </div>

      {/* Divider + Actions */}
      <div className={`mt-5 pt-4 border-t flex flex-wrap gap-3 ${isDark ? "border-white/5" : "border-gray-100"}`}>
        <button
          onClick={logout}
          className={`flex items-center gap-2 text-sm rounded-xl px-4 py-2 transition-colors cursor-pointer whitespace-nowrap ${
            isDark
              ? "bg-white/5 hover:bg-red-400/10 text-gray-400 hover:text-red-400"
              : "bg-gray-100 hover:bg-red-50 text-gray-500 hover:text-red-500"
          }`}
        >
          <i className="ri-logout-box-line text-sm"></i>
          {t("logoutBtn")}
        </button>
      </div>
    </div>
  );
}
